/**
 * Hook — aim a laser independently from cursor movement; launch on click/tap.
 * Pass through balls to collect multiples; retract on wall; lose if a red reaches the cursor.
 */

export const HOOK_NORMAL_START = 15;
export const HOOK_RED_START = 15;
export const HOOK_SPAWN_EVERY_MS = 15000;
export const HOOK_NORMAL_PER_SPAWN = 5;
export const HOOK_RED_PER_SPAWN = 10;
export const HOOK_BALL_SPEED = 0.85;
export const HOOK_BALL_RADIUS_REF = 18;
export const HOOK_RETRACT_MS = 520;
export const HOOK_FLY_SPEED = 1280;
export const HOOK_HIT_PAD = 1.15;
export const HOOK_RED_HEX = "#ff3333";
export const HOOK_RED_GLOW_HEX = "#ff6666";
/** Fallback when no cursor color is passed to the overlay. */
export const HOOK_AIM_LINE_HEX = "#ffffff";

let spawnEveryMs = HOOK_SPAWN_EVERY_MS;
let flySpeedOverride: number | null = null;
let retractMsOverride: number | null = null;

export function setHookTuning(opts?: {
    spawnEveryMs?: number;
    flySpeed?: number;
    retractMs?: number;
}): void {
    spawnEveryMs =
        opts?.spawnEveryMs != null && Number.isFinite(opts.spawnEveryMs)
            ? Math.max(100, Math.floor(opts.spawnEveryMs))
            : HOOK_SPAWN_EVERY_MS;
    flySpeedOverride =
        opts?.flySpeed != null && Number.isFinite(opts.flySpeed) ? Math.max(1, opts.flySpeed) : null;
    retractMsOverride =
        opts?.retractMs != null && Number.isFinite(opts.retractMs)
            ? Math.max(50, Math.floor(opts.retractMs))
            : null;
}

export function getHookFlySpeed(): number {
    return flySpeedOverride ?? HOOK_FLY_SPEED;
}

export function getHookRetractMs(): number {
    return retractMsOverride ?? HOOK_RETRACT_MS;
}

export type HookPhase = "idle" | "flying" | "retracting";

export type HookBallLike = {
    x: number;
    y: number;
    vx?: number;
    vy?: number;
    isHookRed?: boolean;
    collisionRadius: () => number;
};

type RayHit = { dist: number };

let phase: HookPhase = "idle";
let aimAngle = 0;
let tipX = 0;
let tipY = 0;
let flyDirX = 1;
let flyDirY = 0;
const caughtBalls: HookBallLike[] = [];
let retractStartAt = 0;
let retractFromX = 0;
let retractFromY = 0;
let nextSpawnAt = 0;
let hookTipRadius = 4;
/** Catch streak within the current throw (resets when the throw ends). */
let throwCombo = 0;
const pendingCatchEvents: HookCatchEvent[] = [];
const comboPopups: HookComboPopup[] = [];

export type HookCatchEvent = {
    x: number;
    y: number;
    combo: number;
    isRed: boolean;
};

type HookComboPopup = {
    x: number;
    y: number;
    combo: number;
    isRed: boolean;
    bornAt: number;
    tilt: number;
};

function clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
}

/** Keep combo label tilted but never upside-down (readable upright). */
function uprightTilt(angle: number) {
    let a = angle;
    while (a > Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    if (a > Math.PI / 2) a -= Math.PI;
    if (a < -Math.PI / 2) a += Math.PI;
    return a;
}

function isCaught(ball: HookBallLike) {
    return caughtBalls.includes(ball);
}

function rayCircleRoots(
    ox: number,
    oy: number,
    dx: number,
    dy: number,
    cx: number,
    cy: number,
    r: number,
): [number, number] | null {
    const fx = ox - cx;
    const fy = oy - cy;
    const a = dx * dx + dy * dy;
    const b = 2 * (fx * dx + fy * dy);
    const c = fx * fx + fy * fy - r * r;
    const disc = b * b - 4 * a * c;
    if (disc < 0) return null;
    const sqrt = Math.sqrt(disc);
    const t1 = (-b - sqrt) / (2 * a);
    const t2 = (-b + sqrt) / (2 * a);
    return t1 <= t2 ? [t1, t2] : [t2, t1];
}

function rayCircleHit(
    ox: number,
    oy: number,
    dx: number,
    dy: number,
    cx: number,
    cy: number,
    r: number,
): number | null {
    const roots = rayCircleRoots(ox, oy, dx, dy, cx, cy, r);
    if (!roots) return null;
    const hits = roots.filter((t) => t > 1e-4);
    if (hits.length === 0) return null;
    return Math.min(...hits);
}

function rayRectBoundsHit(
    ox: number,
    oy: number,
    dx: number,
    dy: number,
    w: number,
    h: number,
    inset: number,
): number | null {
    const minX = inset;
    const minY = inset;
    const maxX = w - inset;
    const maxY = h - inset;
    let best: number | null = null;
    const tryEdge = (t: number) => {
        if (t <= 1e-4) return;
        if (best == null || t < best) best = t;
    };
    if (Math.abs(dx) > 1e-8) {
        tryEdge((minX - ox) / dx);
        tryEdge((maxX - ox) / dx);
    }
    if (Math.abs(dy) > 1e-8) {
        tryEdge((minY - oy) / dy);
        tryEdge((maxY - oy) / dy);
    }
    if (best == null) return null;
    const hx = ox + dx * best;
    const hy = oy + dy * best;
    if (hx < minX - 1e-3 || hx > maxX + 1e-3 || hy < minY - 1e-3 || hy > maxY + 1e-3) return null;
    return best;
}

function firstBallHitOnSegment(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    balls: HookBallLike[],
    tipR: number,
): { ball: HookBallLike; x: number; y: number; dist: number } | null {
    const segDx = x1 - x0;
    const segDy = y1 - y0;
    const segLen = Math.hypot(segDx, segDy);
    if (segLen < 1e-6) return null;
    const dx = segDx / segLen;
    const dy = segDy / segLen;
    let best: { ball: HookBallLike; x: number; y: number; dist: number } | null = null;
    for (const ball of balls) {
        if (isCaught(ball)) continue;
        const hitR = ball.collisionRadius() + tipR;
        const t = rayCircleHit(x0, y0, dx, dy, ball.x, ball.y, hitR);
        if (t == null || t > segLen + 1e-4) continue;
        const clampT = clamp(t, 0, segLen);
        if (!best || clampT < best.dist) {
            best = { ball, x: x0 + dx * clampT, y: y0 + dy * clampT, dist: clampT };
        }
    }
    return best;
}

/** Aim laser stops only at the play-area edge — passes through balls. */
function castAimRay(
    ox: number,
    oy: number,
    angle: number,
    w: number,
    h: number,
    edgeInset: number,
): RayHit {
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    let best: RayHit = { dist: Math.hypot(w, h) * 2 };
    const wallT = rayRectBoundsHit(ox, oy, dx, dy, w, h, edgeInset);
    if (wallT != null && wallT < best.dist) best = { dist: wallT };
    return best;
}

function attachCaughtBall(ball: HookBallLike, hitX: number, hitY: number) {
    if (isCaught(ball)) return;
    caughtBalls.push(ball);
    ball.vx = 0;
    ball.vy = 0;
    throwCombo += 1;
    const isRed = !!ball.isHookRed;
    const tilt = uprightTilt(aimAngle + (isRed ? -0.55 : -0.42) + (Math.random() - 0.5) * 0.18);
    const ev: HookCatchEvent = { x: hitX, y: hitY, combo: throwCombo, isRed };
    pendingCatchEvents.push(ev);
    comboPopups.push({
        x: hitX,
        y: hitY,
        combo: throwCombo,
        isRed,
        bornAt: performance.now(),
        tilt,
    });
}

export function drainHookCatchEvents(): HookCatchEvent[] {
    if (pendingCatchEvents.length === 0) return [];
    return pendingCatchEvents.splice(0, pendingCatchEvents.length);
}

function endThrowToIdle() {
    phase = "idle";
    throwCombo = 0;
}

/** Exit points on the aim laser just behind each ball the ray passes through. */
function laserPiercePoints(
    ox: number,
    oy: number,
    ex: number,
    ey: number,
    balls: HookBallLike[],
): { x: number; y: number }[] {
    const segDx = ex - ox;
    const segDy = ey - oy;
    const segLen = Math.hypot(segDx, segDy);
    if (segLen < 1e-6) return [];
    const dx = segDx / segLen;
    const dy = segDy / segLen;
    const out: { x: number; y: number }[] = [];
    for (const ball of balls) {
        if (isCaught(ball)) continue;
        const r = Math.max(1, ball.collisionRadius());
        const roots = rayCircleRoots(ox, oy, dx, dy, ball.x, ball.y, r);
        if (!roots) continue;
        const [tNear, tFar] = roots;
        if (tFar < 0 || tNear > segLen) continue;
        // Just behind the ball along the laser (far intersection, nudged slightly past).
        const t = clamp(tFar + Math.min(2.5, r * 0.08), 0, segLen);
        out.push({ x: ox + dx * t, y: oy + dy * t });
    }
    return out;
}

function pruneComboPopups(now: number) {
    const lifeMs = 780;
    for (let i = comboPopups.length - 1; i >= 0; i--) {
        if (now - comboPopups[i].bornAt >= lifeMs) comboPopups.splice(i, 1);
    }
}

function drawComboPopups(ctx: CanvasRenderingContext2D, now: number, layoutScale: number) {
    const lifeMs = 780;
    const ls = layoutScale;
    for (const p of comboPopups) {
        const age = now - p.bornAt;
        if (age < 0 || age >= lifeMs) continue;
        const u = age / lifeMs;
        const fade = u < 0.15 ? u / 0.15 : 1 - (u - 0.15) / 0.85;
        const lift = (10 + 18 * u) * ls;
        const nx = -Math.sin(aimAngle);
        const ny = Math.cos(aimAngle);
        const px = p.x + nx * lift;
        const py = p.y + ny * lift - 6 * ls * u;
        const fontPx = Math.max(14, Math.round(22 * ls * (1 + Math.min(0.35, (p.combo - 1) * 0.04))));
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(p.tilt);
        ctx.globalAlpha = Math.max(0, Math.min(1, fade));
        ctx.font = `800 ${fontPx}px "Segoe UI", system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const label = `x${p.combo}`;
        ctx.lineWidth = Math.max(2, 3.2 * ls);
        ctx.strokeStyle = "rgba(0,0,0,0.55)";
        ctx.strokeText(label, 0, 0);
        ctx.fillStyle = p.isRed ? HOOK_RED_HEX : "#ffffff";
        ctx.shadowColor = p.isRed ? HOOK_RED_GLOW_HEX : "rgba(255,255,255,0.65)";
        ctx.shadowBlur = 8 * ls;
        ctx.fillText(label, 0, 0);
        ctx.restore();
    }
}

function drawPierceDots(
    ctx: CanvasRenderingContext2D,
    points: { x: number; y: number }[],
    lineColor: string,
    layoutScale: number,
) {
    if (points.length === 0) return;
    const ls = layoutScale;
    const r = Math.max(2.2, 3.4 * ls);
    for (const p of points) {
        ctx.save();
        ctx.globalAlpha = 0.95;
        ctx.fillStyle = lineColor;
        ctx.shadowColor = lineColor;
        ctx.shadowBlur = 10 * ls;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 0.42, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
}

function layoutCaughtOnTip() {
    const n = caughtBalls.length;
    if (n <= 1) {
        if (n === 1) {
            caughtBalls[0].x = tipX;
            caughtBalls[0].y = tipY;
        }
        return;
    }
    const spread = Math.min(14, 6 + n * 2);
    caughtBalls.forEach((ball, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI * 0.5;
        ball.x = tipX + Math.cos(a) * spread * 0.35;
        ball.y = tipY + Math.sin(a) * spread * 0.35;
    });
}

export function resetHookState(now: number) {
    phase = "idle";
    aimAngle = 0;
    tipX = 0;
    tipY = 0;
    flyDirX = 1;
    flyDirY = 0;
    caughtBalls.length = 0;
    retractStartAt = 0;
    nextSpawnAt = now + spawnEveryMs;
    throwCombo = 0;
    pendingCatchEvents.length = 0;
    comboPopups.length = 0;
}

export function getHookPhase() {
    return phase;
}

export function getHookCaughtBall() {
    return caughtBalls[0] ?? null;
}

export function getHookCaughtBalls(): readonly HookBallLike[] {
    return caughtBalls;
}

export function isHookInputBlocked() {
    return phase !== "idle";
}

export function hookShouldSkipCursorBallCollision(ball: HookBallLike) {
    return isCaught(ball) && (phase === "flying" || phase === "retracting");
}

export function setHookAimAngle(angle: number) {
    aimAngle = angle;
}

export function nudgeHookAimByMouseDelta(movementX: number, movementY: number, sensitivity = 0.0045) {
    if (Math.abs(movementX) < 1e-6 && Math.abs(movementY) < 1e-6) return;
    setHookAimAngle(aimAngle + movementX * sensitivity);
}

export function pointHookAimAtCanvas(cx: number, cy: number, originX: number, originY: number) {
    const dx = cx - originX;
    const dy = cy - originY;
    if (Math.hypot(dx, dy) < 8) return;
    setHookAimAngle(Math.atan2(dy, dx));
}

export function pointHookAimFromStick(nx: number, ny: number, deadZone = 0.12) {
    const m = Math.hypot(nx, ny);
    if (m < deadZone) return;
    setHookAimAngle(Math.atan2(ny, nx));
}

export function getHookAimAngle() {
    return aimAngle;
}

export function launchHook(cx: number, cy: number) {
    if (phase !== "idle") return false;
    const dx = Math.cos(aimAngle);
    const dy = Math.sin(aimAngle);
    flyDirX = dx;
    flyDirY = dy;
    tipX = cx;
    tipY = cy;
    throwCombo = 0;
    phase = "flying";
    return true;
}

export type HookTickResult =
    | null
    | { type: "red_at_cursor" }
    | { type: "retract_complete"; hadRed: boolean; normalCount: number; balls: HookBallLike[] };

export function tickHook(
    now: number,
    dt: number,
    cx: number,
    cy: number,
    w: number,
    h: number,
    balls: HookBallLike[],
    layoutScale: number,
    edgeInset: number,
    cursorRadius: number,
): HookTickResult {
    hookTipRadius = Math.max(3, 4 * layoutScale);

    if (phase === "flying") {
        const speed = getHookFlySpeed() * layoutScale;
        const travel = speed * dt;
        const maxStep = Math.max(3, 5 * layoutScale);
        const steps = Math.max(1, Math.ceil(travel / maxStep));
        const stepLen = travel / steps;
        const hitR = hookTipRadius * HOOK_HIT_PAD;

        const minX = edgeInset;
        const minY = edgeInset;
        const maxX = w - edgeInset;
        const maxY = h - edgeInset;

        for (let s = 0; s < steps; s++) {
            const prevX = tipX;
            const prevY = tipY;
            const nextX = tipX + flyDirX * stepLen;
            const nextY = tipY + flyDirY * stepLen;

            if (nextX < minX || nextX > maxX || nextY < minY || nextY > maxY) {
                tipX = clamp(nextX, minX, maxX);
                tipY = clamp(nextY, minY, maxY);
                layoutCaughtOnTip();
                if (caughtBalls.length === 0) {
                    endThrowToIdle();
                    return null;
                }
                phase = "retracting";
                retractStartAt = now;
                retractFromX = tipX;
                retractFromY = tipY;
                return null;
            }

            let segAx = prevX;
            let segAy = prevY;
            let segBx = nextX;
            let segBy = nextY;
            for (let guard = 0; guard < 24; guard++) {
                const hit = firstBallHitOnSegment(segAx, segAy, segBx, segBy, balls, hitR);
                if (!hit) {
                    tipX = segBx;
                    tipY = segBy;
                    break;
                }
                attachCaughtBall(hit.ball, hit.x, hit.y);
                tipX = hit.x;
                tipY = hit.y;
                layoutCaughtOnTip();
                const segLen = Math.hypot(segBx - segAx, segBy - segAy);
                if (hit.dist >= segLen - 1e-3) break;
                const ux = (segBx - segAx) / (segLen || 1);
                const uy = (segBy - segAy) / (segLen || 1);
                segAx = hit.x + ux * 0.25;
                segAy = hit.y + uy * 0.25;
            }
            layoutCaughtOnTip();
        }
        return null;
    }

    if (phase === "retracting") {
        const t = clamp((now - retractStartAt) / getHookRetractMs(), 0, 1);
        const ease = t * t * (3 - 2 * t);
        tipX = retractFromX + (cx - retractFromX) * ease;
        tipY = retractFromY + (cy - retractFromY) * ease;
        layoutCaughtOnTip();

        for (const ball of caughtBalls) {
            if (!ball.isHookRed) continue;
            const r = ball.collisionRadius() + cursorRadius;
            if (Math.hypot(ball.x - cx, ball.y - cy) <= r) {
                caughtBalls.length = 0;
                endThrowToIdle();
                return { type: "red_at_cursor" };
            }
        }

        if (t >= 1) {
            const hadRed = caughtBalls.some((b) => b.isHookRed);
            const normalCount = caughtBalls.filter((b) => !b.isHookRed).length;
            const out = [...caughtBalls];
            caughtBalls.length = 0;
            endThrowToIdle();
            return { type: "retract_complete", hadRed, normalCount, balls: out };
        }
    }

    return null;
}

export function shouldHookSpawn(now: number) {
    return now >= nextSpawnAt;
}

export function markHookSpawned(now: number) {
    nextSpawnAt = now + spawnEveryMs;
}

export function shiftHookPauseTimers(deltaMs: number) {
    if (deltaMs <= 0) return;
    retractStartAt = retractStartAt > 0 ? retractStartAt + deltaMs : retractStartAt;
    nextSpawnAt = nextSpawnAt > 0 ? nextSpawnAt + deltaMs : nextSpawnAt;
}

export function drawHookOverlay(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    w: number,
    h: number,
    balls: HookBallLike[],
    layoutScale: number,
    edgeInset: number,
    cursorColor: string = HOOK_AIM_LINE_HEX,
) {
    if (w < 40 || h < 40) return;
    hookTipRadius = Math.max(3, 4 * layoutScale);
    const ls = layoutScale;
    const angle = aimAngle;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    const lineColor = cursorColor || HOOK_AIM_LINE_HEX;
    const now = performance.now();
    pruneComboPopups(now);

    let endX = cx + dx * Math.hypot(w, h);
    let endY = cy + dy * Math.hypot(w, h);
    if (phase === "idle" || phase === "flying") {
        const hit = castAimRay(cx, cy, angle, w, h, edgeInset);
        endX = cx + dx * hit.dist;
        endY = cy + dy * hit.dist;
    } else {
        endX = tipX;
        endY = tipY;
    }

    const lineW = Math.max(1.5, 2.2 * ls);
    ctx.save();
    ctx.lineCap = "round";

    ctx.strokeStyle = lineColor;
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = lineW * 3.2;
    ctx.shadowColor = lineColor;
    ctx.shadowBlur = 14 * ls;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.globalAlpha = 0.95;
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = lineW;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    const pierce = laserPiercePoints(cx, cy, endX, endY, balls);
    drawPierceDots(ctx, pierce, lineColor, ls);

    const hookX = phase === "idle" ? cx + dx * Math.min(18 * ls, Math.hypot(endX - cx, endY - cy) * 0.15) : tipX;
    const hookY = phase === "idle" ? cy + dy * Math.min(18 * ls, Math.hypot(endX - cx, endY - cy) * 0.15) : tipY;
    const hookSize = Math.max(6, 8 * ls);
    ctx.translate(hookX, hookY);
    ctx.rotate(angle + Math.PI * 0.5);
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = Math.max(1.5, 2 * ls);
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(0, hookSize * 0.35, hookSize * 0.55, Math.PI * 0.15, Math.PI * 0.85);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, hookSize * 0.9);
    ctx.lineTo(0, -hookSize * 0.15);
    ctx.stroke();

    ctx.restore();

    drawComboPopups(ctx, now, ls);
}
